import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { MonthGrid, CalendarLegend } from "@/components/calendar/MonthGrid";
import { MiniMonthLegend } from "@/components/calendar/MiniMonthLegend";
import {
  todayISO, parseISO,
  calendarYearPath, calendarMonthPath, calendarDatePath,
} from "@/lib/calendar/dates";
import { getMonthSummary } from "@/lib/calendar/info";
import { SUPPORTED_YEARS, getHolidaysForYear } from "@/data/holidays";
import { FileDown, Loader2 } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import { TodayHero } from "@/components/home/TodayHero";
import { HomeFaq } from "@/components/home/HomeFaq";
import { LongWeekendTable } from "@/components/pages/PseoExtras";
import { getHariPentingForMonth } from "@/data/hari-penting";
import { LazyMount } from "@/components/LazyMount";
import { useState } from "react";

export const Route = createFileRoute("/")({
  head: () => {
    const y = new Date().getFullYear();
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Kalender Indonesia",
        url: "https://hari.co.id/",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://hari.co.id/{search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Kalender Indonesia",
        url: "https://hari.co.id/",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
        ],
      },
    ];
    return {
      meta: [
        { title: `Kalender Indonesia ${y}: Hari Ini, Tanggal Merah & Libur` },
        {
          name: "description",
          content: `Kalender Indonesia ${y} lengkap: hari ini hari apa, besok tanggal berapa, tanggal merah, libur nasional, cuti bersama, Hijriah, Jawa, weton & PDF.`,
        },
        { name: "keywords", content: `besok tanggal berapa, hari ini hari apa, kalender ${y}, libur nasional ${y}, cuti bersama ${y}, tanggal merah ${y}, kalender hijriah, kalender jawa, weton hari ini` },
        { property: "og:title", content: `Kalender Indonesia ${y}: Hari Ini, Tanggal Merah & Libur` },
        { property: "og:description", content: `Kalender Indonesia ${y}: hari ini hari apa, besok tanggal berapa, tanggal merah, libur nasional & cuti bersama.` },
        { name: "twitter:title", content: `Kalender Indonesia ${y}: Hari Ini, Tanggal Merah & Libur` },
        { name: "twitter:description", content: `Kalender Indonesia ${y}: hari ini hari apa, besok tanggal berapa, tanggal merah, libur nasional & cuti bersama.` },
        { property: "og:url", content: "https://hari.co.id/" },
      ],
      links: [{ rel: "canonical", href: "https://hari.co.id/" }],
      scripts: ld.map((obj) => ({
        type: "application/ld+json",
        children: JSON.stringify(obj),
      })),
    };
  },
  component: Index,
});

export function HomePage({ locale }: { locale?: "id" | "en" } = {}) {
  return (
    <SiteShell locale={locale}>
      <IndexBody />
    </SiteShell>
  );
}

function Index() {
  return <HomePage />;
}

function IndexBody() {
  const { t, href, locale } = useLocale();
  const today = parseISO(todayISO())!;
  const todayMonthName = t.common.months[today.m - 1];
  const todayPathMonth = calendarMonthPath(today.y, today.m);
  const summary = getMonthSummary(today.y, today.m);
  const allHolidays = getHolidaysForYear(today.y);
  const hariThisMonth = getHariPentingForMonth(today.m).slice(0, 6);
  const upcoming = allHolidays
    .filter((h) => h.date >= todayISO())
    .slice(0, 6);
  const nextHoliday = allHolidays.find((h) => h.type === "libur" && h.date >= todayISO());

  return (
    <>
      {/* Visible H1 — primary entity for the site */}
      <header className="pt-2">
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
          {locale === "en" ? `Indonesia Calendar ${today.y}` : `Kalender Indonesia ${today.y}`}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {locale === "en"
            ? "Gregorian, Hijri, Javanese & Balinese calendar with public holidays, joint leave, weton and date tools."
            : "Kalender Masehi, Hijriah, Jawa & Bali lengkap dengan tanggal merah, libur nasional, cuti bersama, weton dan kalkulator hari."}
        </p>
      </header>

      {/* Today hero */}
      <section className="mt-4">
        <TodayHero fallbackISO={todayISO()} />
      </section>

      {/* Hari penting bulan ini — ke /hari/<slug> */}
      {hariThisMonth.length > 0 && (
        <section className="mt-6">
          <div className="mb-2 flex items-end justify-between">
            <h2 className="text-base font-semibold">Hari peringatan bulan ini</h2>
            <Link to={"/hari" as never} className="text-xs text-primary hover:underline">
              Lihat hari penting →
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {hariThisMonth.map((h) => {
              const dd = parseInt(h.monthDay.slice(3), 10);
              return (
                <Link
                  key={h.slug}
                  to={`/hari/${h.slug}` as never}
                  className="rounded-md border border-border bg-card px-3 py-2 text-sm hover:border-primary hover:text-primary"
                >
                  <span className="text-muted-foreground">{dd} {t.common.months[today.m - 1]}</span>{" "}
                  <span className="font-medium">{h.name}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Month focus */}
      <section className="mt-8 space-y-4">
        <div className="mx-auto w-full max-w-5xl space-y-3">
          <div className="flex items-end justify-between">
            <h2 className="text-xl font-semibold">
              {todayMonthName} {today.y}
            </h2>
            <CalendarLegend />
          </div>
          <MonthGrid year={today.y} month={today.m} />
          <Link
            to={href(todayPathMonth) as never}
            className="text-sm text-primary hover:underline"
          >
            {t.common.seeDetail} {todayMonthName} {today.y} →
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 font-semibold">{t.home.holidayThisMonth}</h3>
            {summary.holidays.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.home.noHoliday}</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {summary.holidays.map((h) => {
                  const p = parseISO(h.date)!;
                  return (
                    <li key={`${h.date}-${h.name}`} className="flex justify-between gap-2">
                      <Link
                        to={href(calendarDatePath(p.y, p.m, p.d)) as never}
                        className="hover:text-primary"
                        aria-label={`Lihat tanggal ${p.d} ${t.common.months[p.m - 1]} - ${translateHolidayName(h.name, locale)}`}
                      >
                        <span className={h.type === "libur" ? "text-red-date font-medium" : "text-cuti-bersama"}>
                          {p.d} {t.common.months[p.m - 1]}
                        </span>{" "}
                        - {translateHolidayName(h.name, locale)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 font-semibold">{t.home.nextHoliday(today.y)}</h3>
            <ul className="space-y-1.5 text-sm">
              {upcoming.map((h) => {
                const p = parseISO(h.date)!;
                return (
                  <li key={`${h.date}-${h.name}`}>
                    <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
                      <span className="text-muted-foreground">{p.d} {t.common.months[p.m - 1]}</span> - {translateHolidayName(h.name, locale)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Long weekends pSEO */}
      <LazyMount minHeight={300} className="mt-8">
        <section>
          <LongWeekendTable year={today.y} holidays={allHolidays} />
        </section>
      </LazyMount>

      {/* Full current-year calendar (12 months) */}
      <LazyMount minHeight={600} rootMargin="600px" className="mt-8">
      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              {t.home.fullYear(today.y)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t.home.fullYearDesc(today.y)}
            </p>
          </div>
          <Link
            to={href(calendarYearPath(today.y)) as never}
            className="text-sm text-primary hover:underline"
          >
            {t.home.seeYearPage(today.y)} →
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <div key={m} className="space-y-1">
              <Link
                to={href(calendarMonthPath(today.y, m)) as never}
                className="block hover:opacity-90"
                aria-label={`${t.common.calendar} ${t.common.months[m - 1]} ${today.y}`}
              >
                <MonthGrid year={today.y} month={m} showSubLabels={false} />
              </Link>
              <MiniMonthLegend year={today.y} month={m} />
            </div>
          ))}
        </div>
      </section>
      </LazyMount>

      {/* Years quick links */}
      <section className="mt-8">
        <h2 className="mb-3 text-xl font-semibold">{t.common.pickYear}</h2>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_YEARS.map((y) => {
            const isCurrent = y === today.y;
            return (
              <Link
                key={y}
                to={href(calendarYearPath(y)) as never}
                className={
                  isCurrent
                    ? "rounded-md border border-primary bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary"
                    : "rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:border-primary hover:text-primary"
                }
              >
                {y}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Tools */}
      <section className="mt-8 grid gap-4 md:grid-cols-2">
        <Link to={href("/tools/kalkulator-hari") as never} className="rounded-lg border border-border bg-card p-5 hover:border-primary">
          <h3 className="font-semibold">{t.home.toolsCalc}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t.home.toolsCalcDesc}</p>
        </Link>
        <Link to={href("/tools/cek-weton") as never} className="rounded-lg border border-border bg-card p-5 hover:border-primary">
          <h3 className="font-semibold">{t.home.toolsWeton}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t.home.toolsWetonDesc}</p>
        </Link>
      </section>

      {/* PDF teaser */}
      <LazyMount minHeight={120} className="mt-8">
        <section>
          <HomePdfDownload year={today.y} />
        </section>
      </LazyMount>

      {/* FAQ — placed at the bottom so users land on calendar content first */}
      <LazyMount minHeight={400} className="mt-8">
        <section>
          <HomeFaq fallbackISO={todayISO()} nextHoliday={nextHoliday} />
        </section>
      </LazyMount>
    </>
  );
}

function HomePdfDownload({ year }: { year: number }) {
  const { t, locale } = useLocale();
  const [busy, setBusy] = useState(false);
  async function handleDownload() {
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const [{ generateYearPDF }, { downloadPDF }, { toast }] = await Promise.all([
        import("@/lib/pdf/generate"),
        import("@/lib/pdf/pdf-shared"),
        import("sonner"),
      ]);
      const bytes = await generateYearPDF(year, { useDefaultLogo: true, siteUrl: `${origin}/kalender/${year}` });
      downloadPDF(bytes, `kalender-${year}.pdf`);
      void toast;
    } catch (err) {
      console.error("PDF download failed", err);
      const { toast } = await import("sonner");
      toast.error(locale === "en" ? "Failed to generate PDF" : "Gagal membuat PDF");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-border bg-card p-5 sm:flex-row sm:items-center">
      <div className="flex items-start gap-4">
        <FileDown className="mt-1 h-6 w-6 text-primary" />
        <div>
          <h2 className="font-semibold">{t.home.pdfTitle} {year}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t.home.pdfDesc}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:opacity-60"
        aria-label={`${t.home.pdfTitle} ${year}`}
      >
        {busy ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {locale === "en" ? "Preparing…" : "Menyiapkan…"}</>
        ) : (
          <><FileDown className="h-4 w-4" /> {locale === "en" ? `Download ${year}` : `Unduh ${year}`}</>
        )}
      </button>
    </div>
  );
}
